import type { ReactConfigurationGeneratorApi } from '@ingenico/react-configuration-generator';
import { CategoryEnum } from '@ingenico/react-configuration-generator';
import { toString } from 'lodash-es';
import { useCallback, useMemo, useRef } from 'react';
import { filter, Subscription, switchMap, tap } from 'rxjs';
import type { UseConfigurationTabParam, UseConfigurationTabVmOutput } from './configurationTab.model';
import { handleExpandAppAccordion, verifyShouldSubscribeLoadingConfiguration } from './ConfigurationTab.util';

export function useConfigurationTabVm({
  selectedCategory = CategoryEnum.Os,
  selectedApplicationId,
}: UseConfigurationTabParam): UseConfigurationTabVmOutput {
  const isSubscribed = useRef<boolean>(false);
  const subscriptions = useMemo(() => new Subscription(), []);

  const scrollElementToTop = useCallback(
    (element: HTMLDivElement) => {
      const parent = element.closest('[class*=hostContainer]');
      if (parent) {
        const scrollTop = element.offsetTop - (parent as HTMLDivElement).offsetTop + element.offsetHeight * 2;
        setTimeout(() => {
          parent.scrollTo({ top: scrollTop, behavior: 'smooth' });
          subscriptions.unsubscribe();
        }, 500);
      }
    },
    [subscriptions],
  );

  const handleRef = useCallback(
    (config: ReactConfigurationGeneratorApi | null) => {
      if (
        verifyShouldSubscribeLoadingConfiguration({
          hasLoadedRootConfigRef: !!config?.hasLoadedRootConfig$,
          isSubscribed: !!isSubscribed.current,
          selectedApplicationId,
          selectedCategory,
        })
      ) {
        isSubscribed.current = true;
        const subscription = config?.hasLoadedRootConfig$
          .pipe(
            filter((hasLoaded) => !!hasLoaded),
            switchMap(() =>
              config.listenToCategorySelectionChange().pipe(
                filter((emittedSelectedCategory: CategoryEnum) => emittedSelectedCategory === selectedCategory),
                tap(() =>
                  handleExpandAppAccordion(
                    toString(selectedApplicationId),
                    config,
                    selectedCategory,
                    scrollElementToTop,
                  ),
                ),
              ),
            ),
          )
          .subscribe();
        subscriptions.add(subscription);
      }
    },
    [scrollElementToTop, selectedApplicationId, selectedCategory, subscriptions],
  );

  return { handleRef };
}
